import React, { useEffect, useState, createContext, useContext } from 'react';
import { CartItem } from './types';
import { supabase } from './supabase';
interface CartState {
  restaurantId: string | null;
  restaurantName: string | null;
  items: CartItem[];
}
interface CartContextType {
  cart: CartState;
  addItem: (item: Omit<CartItem, 'id'>, restaurantName: string) => boolean;
  removeItem: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
  getTotal: () => number;
  getItemCount: () => number;
  getDiscount: () => {
    amount: number;
    label: string;
  } | null;
}
const CartContext = createContext<CartContextType | undefined>(undefined);
const CART_STORAGE_KEY = 'noiza_cart';
export function CartProvider({
  children
}: {
  children: ReactNode;
}) {
  const [cart, setCart] = useState<CartState>(() => {
    try {
      const saved = localStorage.getItem(CART_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {
        restaurantId: null,
        restaurantName: null,
        items: []
      };
    } catch (e) {
      return {
        restaurantId: null,
        restaurantName: null,
        items: []
      };
    }
  });
  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  }, [cart]);
  // Kitchen pause check — reads from context if available, but also stored locally
  const [kitchenPausedCache, setKitchenPausedCache] = useState(false);
  // Poll kitchen_paused for the current cart's restaurant
  useEffect(() => {
    if (!cart.restaurantId) {
      setKitchenPausedCache(false);
      return;
    }
    const rid = cart.restaurantId;
    const check = async () => {
      try {
        const {
          data
        } = await supabase.from('restaurant_settings').select('kitchen_paused').eq('restaurant_id', rid).single();
        if (data) setKitchenPausedCache(data.kitchen_paused === true);
      } catch {
        /* silent */
      }
    };
    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [cart.restaurantId]);
  const addItem = (newItem: Omit<CartItem, 'id'>, restaurantName: string): boolean => {
    // Block add-to-cart if kitchen is paused
    if (kitchenPausedCache) {
      console.log('[NOIZA][Cart] addItem BLOCKED — kitchen paused for restaurant:', newItem.restaurant_id);
      return false;
    }
    if (cart.restaurantId && cart.restaurantId !== newItem.restaurant_id && cart.items.length > 0) {
      return false;
    }
    setCart(prev => {
      const existingItemIndex = prev.items.findIndex(item => item.menu_item_id === newItem.menu_item_id && JSON.stringify(item.selected_options) === JSON.stringify(newItem.selected_options) && item.notes === newItem.notes);
      if (existingItemIndex >= 0) {
        const newItems = [...prev.items];
        newItems[existingItemIndex].quantity += newItem.quantity;
        return {
          ...prev,
          items: newItems
        };
      }
      return {
        restaurantId: newItem.restaurant_id,
        restaurantName,
        items: [...prev.items, {
          ...newItem,
          id: Math.random().toString(36).substring(2, 9)
        }]
      };
    });
    return true;
  };
  const removeItem = (id: string) => {
    setCart(prev => {
      const newItems = prev.items.filter(item => item.id !== id);
      if (newItems.length === 0) {
        return {
          restaurantId: null,
          restaurantName: null,
          items: []
        };
      }
      return {
        ...prev,
        items: newItems
      };
    });
  };
  const updateQuantity = (id: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(id);
      return;
    }
    setCart(prev => ({
      ...prev,
      items: prev.items.map(item => item.id === id ? {
        ...item,
        quantity
      } : item)
    }));
  };
  const clearCart = () => {
    setCart({
      restaurantId: null,
      restaurantName: null,
      items: []
    });
  };
  const getDiscount = (): {
    amount: number;
    label: string;
  } | null => {
    // Capricho de Canela (restaurant_id = '2') — por cada 2 productos, el más barato al 50%
    if (String(cart.restaurantId) !== '2') return null;
    const totalQty = cart.items.reduce((sum, i) => sum + i.quantity, 0);
    if (totalQty < 2) return null;
    // Number of items that get 50% off = floor(totalQty / 2)
    const numDiscounted = Math.floor(totalQty / 2);
    // Expand all cart lines into individual unit prices
    const allUnitPrices: number[] = [];
    for (const item of cart.items) {
      const optionsTotal = item.selected_options.reduce((sum, opt) => sum + (Number(opt.price_delta) || 0), 0);
      const unitPrice = (Number(item.unit_price_snapshot) || 0) + optionsTotal;
      for (let i = 0; i < item.quantity; i++) {
        allUnitPrices.push(unitPrice);
      }
    }
    // Sort ascending — cheapest first (those get the discount)
    allUnitPrices.sort((a, b) => a - b);
    // Sum 50% off the cheapest N items
    let discountAmount = 0;
    for (let i = 0; i < numDiscounted; i++) {
      discountAmount += Math.round(allUnitPrices[i] * 0.5);
    }
    if (discountAmount <= 0) return null;
    const label = numDiscounted === 1 ? '🎉 2do producto al 50%' : `🎉 ${numDiscounted} productos al 50%`;
    return {
      amount: discountAmount,
      label
    };
  };
  const getTotal = () => {
    const subtotal = cart.items.reduce((total, item) => {
      const optionsTotal = item.selected_options.reduce((sum, opt) => sum + opt.price_delta, 0);
      return total + (item.unit_price_snapshot + optionsTotal) * item.quantity;
    }, 0);
    const discount = getDiscount();
    return subtotal - (discount?.amount ?? 0);
  };
  const getItemCount = () => {
    return cart.items.reduce((count, item) => count + item.quantity, 0);
  };
  return <CartContext.Provider value={{
    cart,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    getTotal,
    getItemCount,
    getDiscount
  }} data-id="element-1425">
      {children}
    </CartContext.Provider>;
}
export function useCart() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}